import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { PaymentService } from '@/services/PaymentService';
import {
  useGetEnvironmentVariableByServiceTypeQuery,
  useGetPaymentOptionsQuery,
} from '@/services/EnvironmentVariablesService';
import {
  ExternalServiceType,
  ModeType,
} from '@/types/environment-variables.types';
import { CouponType } from '@/constant';
import type {
  ICoupon,
  IPaymentRequest,
  IPricingSummaryRequest,
  PaymentContext,
  IPricingSummaryResponse,
} from '@/types/payment.types';
import {
  CreditCard,
  Loader2,
  Tag,
  CheckCircle2,
  ArrowLeft,
} from 'lucide-react';
import StripeCheckoutForm from '@/components/StripeCheckoutForm';
import type { RootState } from '@/store/store';
import {
  formatFinnishCurrency,
  formatFinnishPercentage,
} from '@/utils/Currency';
import { useSettings } from '@/providers';

type PaymentStep = 'select-provider' | 'stripe-checkout' | 'processing';

const SubscriptionPayment: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const paymentContext = location.state as PaymentContext | undefined;
  const { settings } = useSettings();

  const [coupons, setCoupons] = useState<ICoupon[]>([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<ICoupon | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [manualCouponCode, setManualCouponCode] = useState('');
  const [email, setEmail] = useState('');
  const [selectedProvider, setSelectedProvider] = useState<
    'stripe' | 'paytrail' | null
  >(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] =
    useState<PaymentStep>('select-provider');

  // Stripe-specific state
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(
    null
  );
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [validatedCouponData, setValidatedCouponData] = useState<{
    potential_discount: number;
    final_amount: number;
  } | null>(null);
  const [pricingSummary, setPricingSummary] = useState<
    IPricingSummaryResponse['data'] | null
  >(null);

  const [stripePromise, setStripePromise] = useState<ReturnType<
    typeof loadStripe
  > | null>(null);

  // Get logged-in user's email from Redux
  const userData = useSelector((state: RootState) => state.auth.userData);
  const userEmail = userData?.email || '';

  const { data: paymentOptions } = useGetPaymentOptionsQuery();

  const isStripeAvailable = React.useMemo(() => {
    return paymentOptions?.some(
      (option) =>
        option.service_type === ExternalServiceType.STRIPE_PAYMENT_OPTION &&
        option.mode_type === ModeType.ENABLED
    );
  }, [paymentOptions]);

  // Only fetch the publishable key once Stripe is known to be enabled
  const {
    data: stripeEnvVar,
    error: stripeEnvError,
    isLoading: isStripeEnvLoading,
  } = useGetEnvironmentVariableByServiceTypeQuery(
    ExternalServiceType.VITE_STRIPE_PUBLISHABLE_KEY,
    { skip: !isStripeAvailable }
  );

  const isPaytrailAvailable = React.useMemo(() => {
    return paymentOptions?.some(
      (option) =>
        option.service_type === ExternalServiceType.PAYTRAIL_PAYMENT_OPTION &&
        option.mode_type === ModeType.ENABLED
    );
  }, [paymentOptions]);

  useEffect(() => {
    if (!paymentContext) {
      navigate(-1);
      return;
    }

    // Auto-fill email for Paytrail
    if (userEmail) {
      setEmail(userEmail);
    }

    fetchCoupons();
    fetchPricingSummary();
  }, [paymentContext, navigate, userEmail]);

  useEffect(() => {
    if (
      !isStripeAvailable ||
      stripePromise ||
      isStripeEnvLoading ||
      stripeEnvError
    )
      return;

    const publishableKey = stripeEnvVar?.secret_key;
    if (!publishableKey) {
      setError('Stripe publishable key not configured');
      return;
    }

    const stripe = loadStripe(publishableKey);
    setStripePromise(stripe);
  }, [
    isStripeAvailable,
    stripeEnvVar,
    stripePromise,
    isStripeEnvLoading,
    stripeEnvError,
  ]);

  useEffect(() => {
    if (!isStripeAvailable || !stripeEnvError) return;
    setError('Failed to load payment configuration');
  }, [isStripeAvailable, stripeEnvError]);

  const getPricingContextPayload = (): Partial<IPricingSummaryRequest> => {
    if (!paymentContext?.entityId) {
      return {};
    }

    switch (paymentContext.entityType) {
      case 'harbor':
        return { harborId: paymentContext.entityId };
      case 'club':
        return { clubId: paymentContext.entityId };
      case 'service-provider':
        return { serviceProviderId: paymentContext.entityId };
      default:
        return {};
    }
  };

  const getPaymentContextPayload = (): Partial<IPaymentRequest> => {
    if (!paymentContext?.entityId) {
      return {};
    }

    switch (paymentContext.entityType) {
      case 'harbor':
        return { harborId: paymentContext.entityId };
      case 'club':
        return { clubRegistrationRequestId: paymentContext.entityId };
      case 'service-provider':
        return { serviceProviderId: paymentContext.entityId };
      case 'dmo':
        return { dmoRegistrationRequestId: paymentContext.entityId };
      default:
        return {};
    }
  };

  const fetchCoupons = async () => {
    if (!paymentContext) return;

    // Don't fetch coupons for free plans
    if (paymentContext.subscriptionPrice === 0) {
      setLoadingCoupons(false);
      return;
    }

    try {
      setLoadingCoupons(true);
      setError(null);

      const couponTypeMap: Record<string, number> = {
        harbor: CouponType.SUBSCRIPTION_HARBOR,
        club: CouponType.SUBSCRIPTION_CLUB,
        'service-provider': CouponType.SUBSCRIPTION_SERVICE_PROVIDER,
        dmo: CouponType.SUBSCRIPTION_DMO,
      };

      const couponType = couponTypeMap[paymentContext.entityType];
      if (!couponType) {
        throw new Error('Invalid entity type');
      }

      const response = await PaymentService.getCouponsByType(
        couponType,
        paymentContext.subscriptionPrice ?? 0
      );

      if (response.status && response.data) {
        const now = new Date();
        const activeCoupons = response.data.filter((c) => {
          const startDate = new Date(c.start_date);
          const endDate = new Date(c.end_date);
          return now >= startDate && now <= endDate;
        });
        setCoupons(activeCoupons);
      }
    } catch (err) {
      console.error('Error fetching coupons:', err);
      setError('Failed to load available coupons');
    } finally {
      setLoadingCoupons(false);
    }
  };

  const fetchPricingSummary = async (couponCodeFromCaller?: string) => {
    if (!paymentContext) return;

    if (paymentContext.subscriptionPrice === 0) return;

    try {
      setError(null);

      const payload: IPricingSummaryRequest = {
        subscriptionId: paymentContext.subscriptionId,
        ...getPricingContextPayload(),
      };

      if (couponCodeFromCaller) {
        payload.couponCode = couponCodeFromCaller;
      }

      const response = await PaymentService.getPricingSummary(payload);

      if (response.status && response.data) {
        setPricingSummary(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch pricing summary');
      }
    } catch (err: unknown) {
      console.error('Error fetching pricing summary:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch pricing';
      setError(errorMessage);
    }
  };

  const handleApplyCouponCode = async (code: string, coupon?: ICoupon) => {
    const normalizedCode = code.trim();

    if (!paymentContext?.subscriptionPrice) return;
    if (!normalizedCode) {
      setError('Coupon code is required');
      return;
    }

    if (couponCode && couponCode === normalizedCode) {
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const couponTypeMap: Record<string, number> = {
        harbor: CouponType.SUBSCRIPTION_HARBOR,
        club: CouponType.SUBSCRIPTION_CLUB,
        'service-provider': CouponType.SUBSCRIPTION_SERVICE_PROVIDER,
        dmo: CouponType.SUBSCRIPTION_DMO,
      };

      const coupon_type = couponTypeMap[paymentContext.entityType];
      if (!coupon_type) {
        throw new Error('Invalid entity type');
      }

      const response = await PaymentService.validateCoupon({
        code: normalizedCode,
        order_amount: paymentContext.subscriptionPrice,
        coupon_type,
        subscription_id: paymentContext.subscriptionId,
      });

      if (response.status && response.data?.is_valid) {
        const fallbackCoupon: ICoupon = {
          id: `manual-${normalizedCode}`,
          name: normalizedCode,
          code: normalizedCode,
          discount_type: 2,
          coupon_type,
          discount_amount: null,
          discount_percentage: null,
          max_discount: null,
          min_order_value: null,
          start_date: new Date().toISOString(),
          end_date: new Date().toISOString(),
          total_quantity: null,
          remaining_usage_count: null,
          customer_usage_limit: null,
          description: null,
        };

        const appliedCoupon = response.data.coupon || coupon || fallbackCoupon;
        setSelectedCoupon(appliedCoupon);
        setCouponCode(normalizedCode);
        setManualCouponCode(normalizedCode);
        setValidatedCouponData({
          potential_discount: response.data.potential_discount,
          final_amount: response.data.final_amount,
        });

        if (appliedCoupon) {
          setCoupons((prev) => {
            if (prev.some((c) => c.id === appliedCoupon.id)) return prev;
            return [appliedCoupon, ...prev];
          });
        }

        await fetchPricingSummary(normalizedCode);
      } else {
        const messageFromApi = response.data?.message;
        throw new Error(messageFromApi || response.message || 'Invalid coupon');
      }
    } catch (err: unknown) {
      console.error('Error validating coupon:', err);
      let errorMessage = 'Failed to validate coupon';

      if (axios.isAxiosError(err) && err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const handleApplyCoupon = async (coupon: ICoupon) => {
    await handleApplyCouponCode(coupon.code, coupon);
  };

  const handleApplyManualCoupon = async () => {
    const trimmed = manualCouponCode.trim();
    if (!trimmed) {
      setError('Coupon code is required');
      return;
    }
    await handleApplyCouponCode(trimmed);
  };

  const handleRemoveCoupon = () => {
    setSelectedCoupon(null);
    setCouponCode('');
    setManualCouponCode('');
    setValidatedCouponData(null);
    fetchPricingSummary();
  };

  const calculateDiscount = (): number => {
    if (pricingSummary) {
      return pricingSummary.discountAmount;
    }
    if (validatedCouponData) {
      return validatedCouponData.potential_discount;
    }
    return 0;
  };

  const getFinalPrice = (): number => {
    if (pricingSummary) {
      return pricingSummary.grossAmount;
    }
    if (!paymentContext?.subscriptionPrice) return 0;
    if (validatedCouponData) {
      return validatedCouponData.final_amount;
    }
    return paymentContext.subscriptionPrice;
  };

  const handlePayment = async () => {
    if (!paymentContext) return;

    const finalPrice = getFinalPrice();

    // Check if the plan is free (price is 0)
    if (finalPrice === 0) {
      try {
        setProcessing(true);
        setError(null);

        const payload: IPaymentRequest = {
          ...getPaymentContextPayload(),
        };

        if (couponCode) {
          payload.couponCode = couponCode;
        }

        // Call freemium API for free plans
        const response = await PaymentService.activateFreemiumSubscription(
          paymentContext.subscriptionId,
          payload
        );

        if (response.status) {
          // Redirect to success page for free subscription
          navigate(paymentContext.returnUrl || '/', {
            state: { paymentSuccess: true, isFree: true },
            replace: true,
          });
        } else {
          throw new Error(
            response.message || 'Failed to activate free subscription'
          );
        }
      } catch (err: unknown) {
        console.error('Free subscription error:', err);
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to activate subscription. Please try again.';
        setError(errorMessage);
      } finally {
        setProcessing(false);
      }
      return;
    }

    // For paid plans, require payment provider selection
    if (!selectedProvider) return;

    if (selectedProvider === 'paytrail' && !email.trim()) {
      setError('Email is required for Paytrail payments');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const payload: IPaymentRequest = {
        ...getPaymentContextPayload(),
      };

      if (couponCode) {
        payload.couponCode = couponCode;
      }

      if (selectedProvider === 'paytrail') {
        payload.email = email;
      }

      if (paymentContext.returnUrl) {
        payload.returnUrl = paymentContext.returnUrl;
      }

      if (selectedProvider === 'stripe') {
        const response = await PaymentService.payWithStripe(
          paymentContext.subscriptionId,
          payload
        );

        if (response.status && response.client_secret) {
          setStripeClientSecret(response.client_secret);
          setTransactionId(response.transaction_id);
          setCurrentStep('stripe-checkout');
        } else {
          throw new Error(response.message || 'Payment initiation failed');
        }
      } else {
        const response = await PaymentService.payWithPaytrail(
          paymentContext.subscriptionId,
          payload
        );

        if (response.status && response.redirect) {
          // Redirect to Paytrail
          window.location.href = response.redirect;
        } else {
          throw new Error(response.message || 'Payment initiation failed');
        }
      }
    } catch (err: unknown) {
      console.error('Payment error:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Payment failed. Please try again.';
      setError(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const handleBackToSelection = () => {
    setCurrentStep('select-provider');
    setStripeClientSecret(null);
    setTransactionId(null);
    setError(null);
  };

  if (!paymentContext) {
    return null;
  }

  const stripeOptions: StripeElementsOptions = {
    clientSecret: stripeClientSecret || undefined,
    appearance: {
      theme:
        settings.themeMode === 'dark' || settings.themeMode === 'contrast'
          ? 'night'
          : 'stripe',
      variables: {
        colorPrimary: '#2563eb',
      },
    },
  };

  return (
    <div className="min-h-screen  py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() =>
              currentStep === 'stripe-checkout'
                ? handleBackToSelection()
                : navigate(-1)
            }
            className="mb-4 text-gray-600 hover:text-gray-900 flex items-center gap-2 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            {paymentContext.subscriptionPrice === 0 || getFinalPrice() === 0
              ? 'Activate Your Free Subscription'
              : 'Complete Your Subscription'}
          </h1>
          <p className="mt-2 text-gray-600">
            {currentStep === 'stripe-checkout'
              ? 'Enter your payment details securely'
              : paymentContext.subscriptionPrice === 0 || getFinalPrice() === 0
                ? 'Review your free subscription details'
                : 'Choose your payment method and apply any available coupons'}
          </p>
        </div>

        {currentStep === 'stripe-checkout' &&
        stripeClientSecret &&
        stripePromise ? (
          /* Stripe Checkout Step */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Elements stripe={stripePromise} options={stripeOptions}>
                <StripeCheckoutForm
                  transactionId={transactionId || ''}
                  amount={getFinalPrice()}
                  returnUrl={paymentContext.returnUrl}
                  onSuccess={() => {
                    navigate(paymentContext.returnUrl || '/', {
                      state: { paymentSuccess: true },
                    });
                  }}
                  onError={(err) => setError(err)}
                />
              </Elements>
            </div>

            {/* Order Summary - Sticky on right */}
            <div className="lg:col-span-1">
              <div className="card border rounded-xl shadow-md p-6 sticky top-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Order Summary
                </h2>

                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Subscription Plan</p>
                    <p
                      className="font-semibold text-gray-900"
                      dangerouslySetInnerHTML={{
                        __html: paymentContext.subscriptionName,
                      }}
                    />
                  </div>

                  <div className="border-t pt-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-medium">
                        {formatFinnishCurrency(
                          pricingSummary?.netAmount ??
                            paymentContext.subscriptionPrice ??
                            0
                        )}
                      </span>
                    </div>

                    {selectedCoupon && (
                      <div className="flex justify-between text-sm mt-2 text-green-700 dark:text-green-600 contrast:text-green-600">
                        <span>Discount ({selectedCoupon.code})</span>
                        <span className="font-medium">
                          - {formatFinnishCurrency(calculateDiscount())}
                        </span>
                      </div>
                    )}

                    {pricingSummary && (
                      <div className="flex justify-between text-sm mt-2 text-gray-600">
                        <span>
                          VAT ({formatFinnishPercentage(pricingSummary.vatRate)}
                          )
                        </span>
                        <span className="font-medium">
                          {formatFinnishCurrency(pricingSummary.vatAmount)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-3">
                    <div className="flex justify-between">
                      <span className="font-semibold text-gray-900">
                        Total (incl. VAT)
                      </span>
                      <span className="font-bold text-xl text-gray-900">
                        {formatFinnishCurrency(getFinalPrice())}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Provider Selection Step */
          <div
            className={
              getFinalPrice() === 0
                ? 'flex justify-center'
                : 'grid grid-cols-1 lg:grid-cols-3 gap-6'
            }
          >
            {getFinalPrice() > 0 && (
              <div className="lg:col-span-2 space-y-6">
                {/* Payment Method Selection - Only show if price > 0 */}
                {getFinalPrice() > 0 && (
                  <div className="card border rounded-xl shadow-md p-6 mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      Select Payment Method
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {isStripeAvailable && (
                        <button
                          onClick={() => setSelectedProvider('stripe')}
                          className={`relative p-6 rounded-lg border-2 transition-all duration-200 ${
                            selectedProvider === 'stripe'
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-950 contrast:bg-blue-950 shadow-md'
                              : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                          }`}
                        >
                          <div className="flex flex-col items-center gap-3">
                            <div
                              className={`w-16 h-16 rounded-full flex items-center justify-center ${
                                selectedProvider === 'stripe'
                                  ? 'bg-blue-100 dark:bg-blue-900 contrast:bg-blue-900'
                                  : 'bg-gray-100'
                              }`}
                            >
                              <CreditCard className="w-8 h-8" />
                            </div>
                            <div className="text-center">
                              <h3 className="font-semibold text-gray-900">
                                Stripe
                              </h3>
                              <p className="text-sm text-gray-500 mt-1">
                                Credit/Debit Card
                              </p>
                            </div>
                          </div>
                          {selectedProvider === 'stripe' && (
                            <div className="absolute top-3 right-3">
                              <CheckCircle2 className="w-6 h-6 text-blue-500" />
                            </div>
                          )}
                        </button>
                      )}

                      {isPaytrailAvailable && (
                        <button
                          onClick={() => setSelectedProvider('paytrail')}
                          className={`relative p-6 rounded-lg border-2 transition-all duration-200 ${
                            selectedProvider === 'paytrail'
                              ? 'border-green-500 bg-green-50 dark:bg-green-950 contrast:bg-green-950 shadow-md'
                              : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                          }`}
                        >
                          <div className="flex flex-col items-center gap-3">
                            <div
                              className={`w-16 h-16 rounded-full flex items-center justify-center ${
                                selectedProvider === 'paytrail'
                                  ? 'bg-green-100 dark:bg-green-900 contrast:bg-green-900'
                                  : 'bg-gray-100'
                              }`}
                            >
                              <CreditCard className="w-8 h-8" />
                            </div>
                            <div className="text-center">
                              <h3 className="font-semibold text-gray-900">
                                Paytrail
                              </h3>
                              <p className="text-sm text-gray-500 mt-1">
                                Finnish Payment
                              </p>
                            </div>
                          </div>
                          {selectedProvider === 'paytrail' && (
                            <div className="absolute top-3 right-3">
                              <CheckCircle2 className="w-6 h-6 text-green-500" />
                            </div>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Available Coupons - Only show if price > 0 */}
                {getFinalPrice() > 0 && (
                  <div className="card border rounded-xl shadow-md p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Tag className="w-5 h-5" />
                      Available Coupons
                    </h2>

                    <div className="mb-6">
                      <p className="text-sm text-gray-600 mb-2">
                        Have a coupon code?
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <input
                          type="text"
                          value={manualCouponCode}
                          onChange={(e) => setManualCouponCode(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleApplyManualCoupon();
                            }
                          }}
                          placeholder="Enter coupon code"
                          disabled={processing}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <button
                          type="button"
                          onClick={handleApplyManualCoupon}
                          disabled={processing || !manualCouponCode.trim()}
                          className={`w-full sm:w-auto px-4 py-2 rounded-lg font-medium transition-colors ${
                            processing || !manualCouponCode.trim()
                              ? 'bg-gray-300 text-gray-500 contrast:text-gray-900 cursor-not-allowed'
                              : 'bg-blue-500 text-white hover:bg-blue-600'
                          }`}
                        >
                          Apply Code
                        </button>
                      </div>
                    </div>

                    {loadingCoupons ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                      </div>
                    ) : coupons.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">
                        No coupons available at this time
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {coupons.map((coupon) => (
                          <div
                            key={coupon.id}
                            className={`p-4 rounded-lg border-2 transition-all ${
                              selectedCoupon?.id === coupon.id
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-950'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono font-semibold text-lg text-gray-900">
                                    {coupon.code}
                                  </span>
                                  {selectedCoupon?.id === coupon.id && (
                                    <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                                      Applied
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 mt-1">
                                  {coupon.name}
                                </p>
                                {coupon.description && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    {coupon.description}
                                  </p>
                                )}
                                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                  <span>
                                    {coupon.discount_type === 1
                                      ? `${formatFinnishCurrency(
                                          Number(coupon.discount_amount ?? 0)
                                        )} off`
                                      : `${coupon.discount_percentage}% off`}
                                  </span>
                                  {coupon.max_discount && (
                                    <span>
                                      Max:{' '}
                                      {formatFinnishCurrency(
                                        Number(coupon.max_discount ?? 0)
                                      )}
                                    </span>
                                  )}
                                  {coupon.min_order_value && (
                                    <span>
                                      Min:{' '}
                                      {formatFinnishCurrency(
                                        Number(coupon.min_order_value ?? 0)
                                      )}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() =>
                                  selectedCoupon?.id === coupon.id
                                    ? handleRemoveCoupon()
                                    : handleApplyCoupon(coupon)
                                }
                                className={`w-full sm:w-auto px-4 py-2 rounded-lg font-medium transition-colors ${
                                  selectedCoupon?.id === coupon.id
                                    ? 'bg-red-100 text-red-600 hover:bg-red-200'
                                    : 'bg-blue-500 text-white hover:bg-blue-600'
                                }`}
                              >
                                {selectedCoupon?.id === coupon.id
                                  ? 'Remove'
                                  : 'Apply'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Order Summary */}
            <div
              className={
                getFinalPrice() === 0 ? 'w-full max-w-md' : 'lg:col-span-1'
              }
            >
              <div className="card border rounded-xl shadow-md p-6 sticky top-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Order Summary
                </h2>

                <div className="space-y-3 mb-6">
                  <div>
                    <p className="text-sm text-gray-500">Subscription Plan</p>
                    <p
                      className="font-semibold text-gray-900"
                      dangerouslySetInnerHTML={{
                        __html: paymentContext.subscriptionName,
                      }}
                    />
                  </div>

                  <div className="border-t pt-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-medium">
                        {formatFinnishCurrency(
                          pricingSummary?.netAmount ??
                            paymentContext.subscriptionPrice ??
                            0
                        )}
                      </span>
                    </div>

                    {selectedCoupon && (
                      <div className="flex justify-between text-sm mt-2 text-green-700 dark:text-green-600 contrast:text-green-600">
                        <span>Discount ({selectedCoupon.code})</span>
                        <span className="font-medium">
                          - {formatFinnishCurrency(calculateDiscount())}
                        </span>
                      </div>
                    )}

                    {pricingSummary && (
                      <div className="flex justify-between text-sm mt-2 text-gray-600">
                        <span>
                          VAT ({formatFinnishPercentage(pricingSummary.vatRate)}
                          )
                        </span>
                        <span className="font-medium">
                          {formatFinnishCurrency(pricingSummary.vatAmount)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-3">
                    <div className="flex justify-between">
                      <span className="font-semibold text-gray-900">
                        Total (incl. VAT)
                      </span>
                      <span className="font-bold text-xl text-gray-900">
                        {formatFinnishCurrency(getFinalPrice())}
                      </span>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-900 contrast:bg-red-900 border border-red-200 dark:border-red-800 contrast:border-red-800 rounded-lg">
                    <p className="text-sm text-red-600 dark:text-red-200 contrast:text-red-200">
                      {error}
                    </p>
                  </div>
                )}

                {getFinalPrice() === 0 && (
                  <div className="mb-4 p-3 bg-green-50 dark:bg-green-950 contrast:bg-green-950 border border-green-200 dark:border-green-800 contrast:border-green-800 rounded-lg">
                    <p className="text-sm text-green-700 dark:text-green-200 font-medium contrast:text-green-200">
                      🎉 This plan is free! No payment required.
                    </p>
                  </div>
                )}

                <button
                  onClick={handlePayment}
                  disabled={
                    (getFinalPrice() > 0 && !selectedProvider) || processing
                  }
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-all ${
                    (getFinalPrice() > 0 && !selectedProvider) || processing
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md hover:shadow-lg'
                  }`}
                >
                  {processing ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {getFinalPrice() === 0
                        ? 'Activating...'
                        : 'Processing...'}
                    </span>
                  ) : getFinalPrice() === 0 ? (
                    'Activate Free Plan'
                  ) : (
                    'Continue to Payment'
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center mt-4">
                  {getFinalPrice() === 0 ? (
                    'No payment information required'
                  ) : (
                    <>
                      Secure payment powered by{' '}
                      {selectedProvider === 'stripe'
                        ? 'Stripe'
                        : selectedProvider === 'paytrail'
                          ? 'Paytrail'
                          : 'your selected provider'}
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPayment;
